import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/utils/api";
import {
  formatCOPShort, formatRelative, getScoreBadge,
  getUrgencyColor, getAIPriorityBadge, requiresRup,
} from "@/utils/formatting";
import { TEST_IDS } from "@/constants/testIds";
import { Search, SlidersHorizontal, ChevronLeft, ChevronRight, X, Brain, ShieldOff, RefreshCw, MapPin, DollarSign, Clock, Info } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const SCORE_OPTIONS = [
  { label: "Todos los scores", value: "" },
  { label: "🔥 Alta probabilidad (85+)", value: "FIRE" },
  { label: "🟢 Viable (70+)", value: "GREEN" },
  { label: "🟡 Evaluar (50+)", value: "YELLOW" },
  { label: "⚪ Baja prioridad", value: "WHITE" },
];

const AI_PRIORITY_OPTIONS = [
  { label: "Todas las prioridades IA", value: "" },
  { label: "● Alta Prioridad IA", value: "ALTA_PRIORIDAD" },
  { label: "● Revisar (IA)", value: "REVISAR" },
  { label: "● No Recomendado (IA)", value: "NO_RECOMENDADO" },
];

const SORT_OPTIONS = [
  { label: "Score", value: "score" },
  { label: "Score IA", value: "ai_score" },
  { label: "Fecha cierre", value: "closing_date" },
  { label: "Valor", value: "value" },
  { label: "Publicación", value: "publication_date" },
];

export default function RadarView() {
  const [searchParams] = useSearchParams();
  const [processes, setProcesses] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [neverSynced, setNeverSynced] = useState(false);

  // Debounced search: inputValue drives the UI, q triggers the API
  const [inputValue, setInputValue] = useState("");
  const [q, setQ] = useState("");
  const debounceTimer = useRef(null);

  const [department, setDepartment] = useState("");
  const [category, setCategory] = useState("");
  const [aiPriority, setAiPriority] = useState(() => searchParams.get("ai_priority") || "");
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");
  const [sort, setSort] = useState("score");
  const [showFilters, setShowFilters] = useState(false);   // desktop inline panel
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false); // mobile sheet
  const [filterOptions, setFilterOptions] = useState({ departments: [], statuses: [] });
  const [sinRup, setSinRup] = useState(() => localStorage.getItem("radarlit_sin_rup") === "true");

  // Debounce search input (350ms)
  const handleSearchChange = (value) => {
    setInputValue(value);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setQ(value), 350);
  };

  const clearSearch = () => {
    setInputValue("");
    setQ("");
  };

  const clearAllFilters = () => {
    setInputValue(""); setQ("");
    setMinValue(""); setMaxValue(""); setDepartment("");
    setCategory(""); setAiPriority(""); setSinRup(false);
    localStorage.setItem("radarlit_sin_rup", "false");
  };

  const fetchFilterOptions = useCallback(() => {
    api.get("/processes/stats/filters").then((r) => setFilterOptions(r.data)).catch(() => {});
  }, []);

  // Fetch last sync info once on mount
  useEffect(() => {
    api.get("/dashboard/stats").then((r) => {
      setLastSync(r.data.last_sync || null);
      setNeverSynced(r.data.sync_status === "NEVER");
    }).catch(() => {});
  }, []);

  const fetchProcesses = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 20, sort, order: "desc" };
    if (q) params.q = q;
    if (department) params.department = department;
    if (category) params.category = category;
    if (aiPriority) params.ai_priority = aiPriority;
    if (minValue) params.min_value = Number(minValue);
    if (maxValue) params.max_value = Number(maxValue);
    if (sinRup) params.sin_rup = true;

    api
      .get("/processes", { params })
      .then((r) => {
        setProcesses(r.data.results || []);
        setTotal(r.data.total || 0);
        setPages(r.data.pages || 1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, q, department, category, aiPriority, minValue, maxValue, sort, sinRup]);

  useEffect(() => { fetchFilterOptions(); }, [fetchFilterOptions]);

  useEffect(() => {
    setPage(1);
  }, [q, department, category, aiPriority, minValue, maxValue, sinRup]);

  useEffect(() => { fetchProcesses(); }, [fetchProcesses]);

  const activeFilters = [department, category, aiPriority, minValue, maxValue, sinRup].filter(Boolean).length;
  const hasAnyFilter = !!(q || department || category || aiPriority || minValue || maxValue || sinRup);

  const formatLastSync = (isoStr) => {
    if (!isoStr) return null;
    const d = new Date(isoStr);
    const now = new Date();
    const diff = now - d;
    const hours = Math.floor(diff / 3600000);
    if (hours < 1) return "hace menos de 1 hora";
    if (hours < 24) return `hace ${hours}h`;
    const days = Math.floor(hours / 24);
    if (days === 1) return "ayer";
    return `hace ${days} días`;
  };

  // Shared filter panel content (used in both desktop inline and mobile sheet)
  const FilterPanelContent = () => (
    <div className="grid grid-cols-1 gap-4">
      {/* Dept */}
      <div>
        <label className="text-slate-400 text-xs mb-1 block">Departamento</label>
        <select
          data-testid={TEST_IDS.RADAR_DEPT_FILTER}
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          className="w-full bg-slate-800 border border-slate-700 text-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
        >
          <option value="">Todos los departamentos</option>
          {filterOptions.departments.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      </div>
      {/* Score */}
      <div>
        <label className="text-slate-400 text-xs mb-1 block">Categoría de Score</label>
        <select
          data-testid={TEST_IDS.RADAR_SCORE_FILTER}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full bg-slate-800 border border-slate-700 text-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
        >
          {SCORE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {/* AI Priority */}
      <div>
        <label className="text-slate-400 text-xs mb-1 block">Prioridad IA</label>
        <select
          data-testid="radar-ai-priority-filter"
          value={aiPriority}
          onChange={(e) => setAiPriority(e.target.value)}
          className="w-full bg-slate-800 border border-slate-700 text-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
        >
          {AI_PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {/* Values */}
      <div>
        <label className="text-slate-400 text-xs mb-1 block">Valor mínimo (COP)</label>
        <input type="number" value={minValue} onChange={(e) => setMinValue(e.target.value)}
          placeholder="50.000.000"
          className="w-full bg-slate-800 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
        />
      </div>
      <div>
        <label className="text-slate-400 text-xs mb-1 block">Valor máximo (COP)</label>
        <input type="number" value={maxValue} onChange={(e) => setMaxValue(e.target.value)}
          placeholder="10.000.000.000"
          className="w-full bg-slate-800 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
        />
      </div>
      {/* Sort */}
      <div>
        <label className="text-slate-400 text-xs mb-1 block">Ordenar por</label>
        <select value={sort} onChange={(e) => setSort(e.target.value)}
          className="w-full bg-slate-800 border border-slate-700 text-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
        >
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {/* Sin RUP */}
      <button
        data-testid="radar-sin-rup-btn"
        onClick={() => {
          const next = !sinRup;
          setSinRup(next);
          localStorage.setItem("radarlit_sin_rup", next ? "true" : "false");
        }}
        className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all w-full justify-center ${
          sinRup
            ? "bg-blue-500/20 border-blue-400/50 text-blue-300"
            : "bg-slate-800 border-slate-700 text-slate-400 hover:text-blue-300 hover:border-blue-500/40"
        }`}
      >
        <ShieldOff className="w-4 h-4" />
        Sin RUP {sinRup && <span className="text-[10px] bg-blue-400/20 px-1.5 py-0.5 rounded-full">Activo</span>}
      </button>
      {/* Clear */}
      <button onClick={clearAllFilters}
        className="w-full bg-slate-700/50 hover:bg-slate-700 border border-slate-600 text-slate-400 rounded-lg py-2 text-sm transition-colors"
      >
        Limpiar todos los filtros
      </button>
    </div>
  );

  return (
    <div data-testid={TEST_IDS.RADAR_PAGE} className="p-3 lg:p-6 space-y-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-outfit font-bold text-xl sm:text-2xl text-slate-50">Radar de Procesos</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            {loading ? "Buscando..." : total > 0
              ? `${total.toLocaleString("es-CO")} procesos encontrados`
              : "Sin resultados"}
          </p>
        </div>
        {lastSync && (
          <div className="text-right flex-shrink-0">
            <p className="text-slate-500 text-xs flex items-center gap-1 justify-end">
              <RefreshCw className="w-3 h-3" />
              Actualizado {formatLastSync(lastSync)}
            </p>
          </div>
        )}
        {neverSynced && (
          <p className="text-amber-400 text-xs">Sincronización pendiente</p>
        )}
      </div>

      {/* ── Desktop search + filters bar ── */}
      <div className="hidden md:flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid={TEST_IDS.RADAR_SEARCH}
            type="text"
            value={inputValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Buscar por objeto, entidad, proceso..."
            className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg pl-9 pr-4 py-2 text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50"
          />
          {inputValue && (
            <button onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <select data-testid="radar-ai-priority-filter" value={aiPriority} onChange={(e) => setAiPriority(e.target.value)}
          className={`bg-slate-900 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 transition-colors ${
            aiPriority ? "border-emerald-500/40 text-emerald-300" : "border-slate-700 text-slate-300"
          }`}>
          {AI_PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <select data-testid={TEST_IDS.RADAR_SCORE_FILTER} value={category} onChange={(e) => setCategory(e.target.value)}
          className="bg-slate-900 border border-slate-700 text-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30">
          {SCORE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <select data-testid={TEST_IDS.RADAR_DEPT_FILTER} value={department} onChange={(e) => setDepartment(e.target.value)}
          className="bg-slate-900 border border-slate-700 text-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30">
          <option value="">Todos los dptos.</option>
          {filterOptions.departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>

        <button onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
            activeFilters > 0
              ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-400"
              : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-200 hover:bg-slate-800"
          }`}>
          <SlidersHorizontal className="w-4 h-4" />
          Más filtros {activeFilters > 0 && `(${activeFilters})`}
        </button>

        <button data-testid="radar-sin-rup-btn"
          onClick={() => { const next = !sinRup; setSinRup(next); localStorage.setItem("radarlit_sin_rup", next ? "true" : "false"); }}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
            sinRup ? "bg-blue-500/20 border-blue-400/50 text-blue-300" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-blue-300 hover:border-blue-500/40"
          }`}>
          <ShieldOff className="w-4 h-4" />
          Sin RUP {sinRup && <span className="text-[10px] bg-blue-400/20 px-1.5 py-0.5 rounded-full">ON</span>}
        </button>
      </div>

      {/* Desktop advanced filters panel */}
      {showFilters && (
        <div className="hidden md:grid bg-slate-900 border border-slate-700/50 rounded-xl p-4 grid-cols-2 md:grid-cols-4 gap-4 animate-fade-in-up">
          <div>
            <label className="text-slate-400 text-xs mb-1 block">Valor mínimo (COP)</label>
            <input type="number" value={minValue} onChange={(e) => setMinValue(e.target.value)} placeholder="50.000.000"
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
          </div>
          <div>
            <label className="text-slate-400 text-xs mb-1 block">Valor máximo (COP)</label>
            <input type="number" value={maxValue} onChange={(e) => setMaxValue(e.target.value)} placeholder="10.000.000.000"
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
          </div>
          <div>
            <label className="text-slate-400 text-xs mb-1 block">Ordenar por</label>
            <select value={sort} onChange={(e) => setSort(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 text-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
              {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={clearAllFilters}
              className="w-full bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-400 rounded-lg py-2 text-sm transition-colors">
              Limpiar filtros
            </button>
          </div>
        </div>
      )}

      {/* ── Mobile search + filter button ── */}
      <div className="flex md:hidden gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid={TEST_IDS.RADAR_SEARCH}
            type="text"
            value={inputValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Buscar procesos..."
            className="w-full bg-slate-900 border border-slate-700 text-slate-200 rounded-lg pl-9 pr-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          />
          {inputValue && (
            <button onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button onClick={() => setMobileFiltersOpen(true)}
          className={`flex items-center gap-1.5 px-3 py-2.5 rounded-lg border text-sm transition-colors shrink-0 ${
            activeFilters > 0
              ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-400"
              : "bg-slate-900 border-slate-700 text-slate-400"
          }`}>
          <SlidersHorizontal className="w-4 h-4" />
          {activeFilters > 0 ? activeFilters : ""}
        </button>
      </div>

      {/* Mobile filter sheet */}
      <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
        <SheetContent side="right" className="bg-slate-900 border-slate-700 text-slate-200 overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-slate-100 font-outfit">Filtros</SheetTitle>
          </SheetHeader>
          <FilterPanelContent />
        </SheetContent>
      </Sheet>

      {/* ── Empty states ── */}
      {!loading && processes.length === 0 && (
        <div className="bg-slate-900 border border-slate-700/50 rounded-xl p-10 text-center">
          {neverSynced ? (
            <>
              <RefreshCw className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-300 font-medium">Preparando tu Radar</p>
              <p className="text-slate-500 text-sm mt-1">Todavía no se ha ejecutado la primera sincronización con SECOP II.</p>
              <p className="text-slate-600 text-xs mt-2">Ve al Dashboard y presiona "Sincronizar SECOP II" para cargar procesos.</p>
            </>
          ) : hasAnyFilter ? (
            <>
              <Search className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-300 font-medium">No encontramos procesos con estos filtros</p>
              <p className="text-slate-500 text-sm mt-1">Prueba ajustando los criterios de búsqueda:</p>
              <div className="flex flex-wrap gap-2 justify-center mt-4">
                {department && <button onClick={() => setDepartment("")} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-400 rounded-lg text-xs transition-colors">Quitar departamento</button>}
                {sinRup && <button onClick={() => { setSinRup(false); localStorage.setItem("radarlit_sin_rup","false"); }} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-400 rounded-lg text-xs transition-colors">Quitar filtro Sin RUP</button>}
                {(minValue || maxValue) && <button onClick={() => { setMinValue(""); setMaxValue(""); }} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-400 rounded-lg text-xs transition-colors">Ampliar rango de valor</button>}
                {(q || category || aiPriority) && <button onClick={clearAllFilters} className="px-3 py-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-400 rounded-lg text-xs transition-colors">Limpiar todos los filtros</button>}
              </div>
            </>
          ) : (
            <>
              <Brain className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-300 font-medium">No hay procesos disponibles</p>
              <p className="text-slate-500 text-sm mt-1">Los procesos SECOP II aparecerán aquí después de la próxima sincronización.</p>
            </>
          )}
        </div>
      )}

      {/* ── Desktop table (hidden on mobile) ── */}
      {(loading || processes.length > 0) && (
        <div className="hidden md:block bg-slate-900 border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid={TEST_IDS.RADAR_TABLE} className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="px-4 py-3 text-left text-slate-400 text-xs font-medium w-28">Índice</th>
                  <th className="px-4 py-3 text-left text-slate-400 text-xs font-medium hidden sm:table-cell w-28">
                    <span className="flex items-center gap-1"><Brain className="w-3 h-3" /> IA</span>
                  </th>
                  <th className="px-4 py-3 text-left text-slate-400 text-xs font-medium">Objeto / Entidad</th>
                  <th className="px-4 py-3 text-left text-slate-400 text-xs font-medium hidden md:table-cell">Dpto.</th>
                  <th className="px-4 py-3 text-right text-slate-400 text-xs font-medium hidden lg:table-cell">Valor</th>
                  <th className="px-4 py-3 text-right text-slate-400 text-xs font-medium">Cierre</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500 text-sm">Cargando procesos...</td></tr>
                ) : (
                  processes.map((p) => <ProcessRow key={p.id} process={p} />)
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Mobile cards (hidden on desktop) ── */}
      {!loading && processes.length > 0 && (
        <div className="block md:hidden space-y-3">
          {processes.map((p) => <MobileProcessCard key={p.id} process={p} />)}
        </div>
      )}

      {loading && (
        <div className="block md:hidden space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="bg-slate-900 border border-slate-700/50 rounded-xl p-4 animate-pulse">
              <div className="h-4 bg-slate-800 rounded w-3/4 mb-2" />
              <div className="h-3 bg-slate-800 rounded w-1/2" />
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div data-testid={TEST_IDS.RADAR_PAGINATION} className="flex items-center justify-between">
          <p className="text-slate-500 text-xs">
            Página {page} de {pages} · {total.toLocaleString("es-CO")} resultados
          </p>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="p-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-400 hover:text-slate-200 disabled:opacity-40 transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages}
              className="p-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-400 hover:text-slate-200 disabled:opacity-40 transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Score badge with dual-score tooltip ── */
function ScoreIndexBadge({ process }) {
  const [open, setOpen] = useState(false);
  const badge = getScoreBadge(process.score_category, process.score);
  const rel = process.relevance_score;
  const compat = process.compatibility_score;
  const hasV2 = rel != null && compat != null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="text-left cursor-pointer group/badge"
        aria-label="Ver desglose del Índice RadarLit"
      >
        <span className={`text-[10px] px-2 py-1 rounded-full font-medium whitespace-nowrap ${badge.cls}`}>
          {badge.label}
        </span>
        <p className="font-mono text-slate-500 text-[10px] mt-0.5 flex items-center gap-0.5">
          {process.score}/100 <Info className="w-2.5 h-2.5 opacity-50 group-hover/badge:opacity-100 transition-opacity" />
        </p>
      </button>

      {/* Tooltip */}
      {open && hasV2 && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-slate-800 border border-slate-600 rounded-xl p-3 min-w-[220px] shadow-xl text-left">
          <p className="text-slate-300 text-[10px] font-semibold mb-2 uppercase tracking-wide">Índice RadarLit</p>
          <p className="text-slate-500 text-[9px] mb-2 italic">Estimación basada en datos disponibles SECOP. No garantiza viabilidad.</p>
          <div className="space-y-2">
            <div>
              <div className="flex justify-between items-center mb-0.5">
                <span className="text-slate-400 text-[10px]">Relevancia</span>
                <span className="text-emerald-400 text-[10px] font-mono">{rel}/100</span>
              </div>
              <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${rel}%` }} />
              </div>
              {process.relevance_factors?.[0] && (
                <p className="text-slate-600 text-[9px] mt-0.5">{process.relevance_factors[0]}</p>
              )}
            </div>
            <div>
              <div className="flex justify-between items-center mb-0.5">
                <span className="text-slate-400 text-[10px]">Compatibilidad</span>
                <span className="text-blue-400 text-[10px] font-mono">{compat}/100</span>
              </div>
              <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${compat}%` }} />
              </div>
              {process.compatibility_factors?.[0] && (
                <p className="text-slate-600 text-[9px] mt-0.5">{process.compatibility_factors[0]}</p>
              )}
            </div>
          </div>
          <p className="text-slate-700 text-[8px] mt-2 border-t border-slate-700 pt-1">
            Pendiente de verificar en documentos oficiales del proceso.
          </p>
        </div>
      )}
    </div>
  );
}

/* ── Desktop table row ── */
function ProcessRow({ process }) {
  const urgencyColor = getUrgencyColor(process.closing_date);
  const aiBadge = process.ai_priority ? getAIPriorityBadge(process.ai_priority) : null;
  const rupReq = requiresRup(process.modality);

  return (
    <tr data-testid={TEST_IDS.RADAR_ROW} className="border-b border-slate-700/30 hover:bg-slate-800/40 transition-colors group">
      <td className="px-4 py-3">
        <ScoreIndexBadge process={process} />
      </td>
      <td className="px-4 py-3 hidden sm:table-cell">
        <Link to={`/procesos/${process.id}`} className="block">
          {aiBadge ? (
            <div>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${aiBadge.cls}`}>{aiBadge.label}</span>
              {process.ai_score != null && <p className="font-mono text-slate-500 text-[9px] mt-0.5">{process.ai_score}/100</p>}
            </div>
          ) : <span className="text-slate-700 text-[9px]">—</span>}
        </Link>
      </td>
      <td className="px-4 py-3 max-w-xs">
        <Link to={`/procesos/${process.id}`} className="block">
          <p className="text-slate-200 text-sm font-medium line-clamp-2 group-hover:text-emerald-300 transition-colors">
            {process.object || "Sin descripción"}
          </p>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <p className="text-slate-500 text-xs truncate">{process.entity}</p>
            {rupReq === "NO_APLICA" && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/20 whitespace-nowrap">Sin RUP</span>
            )}
            {rupReq === "REQUERIDO" && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-700/50 text-slate-500 whitespace-nowrap">RUP requerido</span>
            )}
          </div>
        </Link>
      </td>
      <td className="px-4 py-3 hidden md:table-cell">
        <span className="text-slate-400 text-xs">{process.department || "—"}</span>
      </td>
      <td className="px-4 py-3 text-right hidden lg:table-cell">
        <span className="font-mono text-slate-300 text-xs">{formatCOPShort(process.value)}</span>
      </td>
      <td className="px-4 py-3 text-right">
        <Link to={`/procesos/${process.id}`} className="block">
          <span className={`text-xs font-medium ${urgencyColor}`}>{formatRelative(process.closing_date)}</span>
        </Link>
      </td>
    </tr>
  );
}

/* ── Mobile card ── */
function MobileProcessCard({ process }) {
  const badge = getScoreBadge(process.score_category, process.score);
  const urgencyColor = getUrgencyColor(process.closing_date);
  const aiBadge = process.ai_priority ? getAIPriorityBadge(process.ai_priority) : null;
  const rupReq = requiresRup(process.modality);

  return (
    <Link
      to={`/procesos/${process.id}`}
      data-testid={TEST_IDS.RADAR_ROW}
      className="block bg-slate-900 border border-slate-700/50 rounded-xl p-4 hover:border-emerald-500/30 hover:bg-slate-800/60 transition-all active:scale-[0.99]"
    >
      {/* Top row: score + AI badge + RUP */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
        <span className="text-slate-500 font-mono text-[10px]">{process.score}/100</span>
        {aiBadge && <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${aiBadge.cls}`}>{aiBadge.label}</span>}
        {rupReq === "NO_APLICA" && (
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/20">Sin RUP</span>
        )}
      </div>

      {/* Title */}
      <p className="text-slate-200 text-sm font-medium line-clamp-2 mb-1">
        {process.object || "Sin descripción"}
      </p>

      {/* Meta */}
      <p className="text-slate-500 text-xs mb-2 truncate">{process.entity}</p>

      {/* Footer row */}
      <div className="flex items-center gap-3 flex-wrap">
        {process.department && (
          <span className="flex items-center gap-1 text-slate-500 text-xs">
            <MapPin className="w-3 h-3" />{process.department}
          </span>
        )}
        {process.value > 0 && (
          <span className="flex items-center gap-1 text-slate-400 text-xs font-mono">
            <DollarSign className="w-3 h-3" />{formatCOPShort(process.value)}
          </span>
        )}
        <span className={`flex items-center gap-1 text-xs font-medium ml-auto ${urgencyColor}`}>
          <Clock className="w-3 h-3" />{formatRelative(process.closing_date)}
        </span>
      </div>
    </Link>
  );
}

